// ============================================================================
// HOLIDAY MASTER SCREEN
// ============================================================================
// Master CRUD screen for prima.basic.tbl_holiday via prima-api
// /api/v1/Holidays endpoint.
//
// Follows the standard master screen blueprint (EngagementTypeMasterScreen):
//  - ModuleHeader with title, description, icon, search, filter, actions
//  - SyutGrid with server-side pagination (manualPagination)
//  - Filter panel with Year, Location, Status filters
//  - Row actions: Edit, Delete (via ActionMenu)
//  - Add opens HolidayBulkAddModal (not the single-record form)
//  - Edit opens HolidayFormModal in 'edit' mode

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { weekdayShort, weekdayLong } from 'utils';
import {
  useHolidays,
  useUpdateHoliday,
  useDeleteHoliday,
} from 'hooks/api/useHoliday';
import { useOffices } from 'hooks/api/useOffice';
import { useCountries } from 'hooks/api/useCountry';
import { useStateProvinces } from 'hooks/api/useStateProvince';
import type {
  Holiday,
  HolidaySearchRequest,
  UpdateHolidayRequest,
} from 'api/types';
import HolidayBulkAddModal from 'components/modals/modules/holiday/HolidayBulkAddModal';
import HolidayFormModal, {
  type HolidayFormData,
} from 'components/modals/modules/holiday/HolidayFormModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CURRENT_YEAR = new Date().getFullYear();

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const YEAR_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Years' },
  ...Array.from({ length: 11 }, (_, i) => {
    const y = CURRENT_YEAR - 5 + i;
    return { value: String(y), label: String(y) };
  }),
];

/** Format ISO date to DD MMM YYYY. */
const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const columnDefinitions: ColumnDefinition[] = [
  { id: 'year',        label: 'Year',     field: 'year',        required: true,  defaultVisible: true  },
  { id: 'officeName',  label: 'Location', field: 'officeName',  required: true,  defaultVisible: true  },
  { id: 'holidayDate', label: 'Date',     field: 'holidayDate', required: false, defaultVisible: true  },
  { id: 'day',         label: 'Day',      field: 'holidayDate', required: false, defaultVisible: true  },
  { id: 'description', label: 'Holiday',  field: 'description', required: false, defaultVisible: true  },
  { id: 'holidayType', label: 'Type',     field: 'holidayType', required: false, defaultVisible: true  },
  { id: 'isOptional',  label: 'Optional', field: 'isOptional',  required: false, defaultVisible: false },
  { id: 'status',      label: 'Status',   field: 'statusName',  required: false, defaultVisible: true  },
  { id: 'createdAt',   label: 'Created',  field: 'createdAt',   required: false, defaultVisible: false },
  { id: 'updatedAt',   label: 'Updated',  field: 'updatedAt',   required: false, defaultVisible: false },
];

const COLUMN_PREF_KEY = 'holiday-master-columns';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface HolidayMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HolidayMasterScreen: React.FC<HolidayMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --------------------------------------------------------------------------
  // Column visibility state (persisted to localStorage)
  // --------------------------------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore corrupt prefs */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // --------------------------------------------------------------------------
  // Server-side query state
  // --------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<HolidaySearchRequest>({
    page: 1,
    pageSize: 25,
    year: CURRENT_YEAR,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useHolidays(queryParams);

  // --------------------------------------------------------------------------
  // Office lookup for filters + bulk add modal
  // --------------------------------------------------------------------------
  const { data: officesData } = useOffices({ pageSize: 500, includeInactive: false });
  const { data: countries } = useCountries();
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const { data: states } = useStateProvinces(selectedCountryId || null);

  // Simple options for filters
  const officeOptions = useMemo(
    () => officesData.map((o) => ({ value: o.id, label: o.name })),
    [officesData],
  );

  // Rich options with geo for bulk add modal
  const officeOptionsWithGeo = useMemo(
    () => officesData.map((o) => ({
      value: o.id,
      label: o.name,
      countryId: o.countryId || undefined,
      countryName: o.countryName || undefined,
      stateId: o.stateId || undefined,
      stateName: o.stateName || undefined,
    })),
    [officesData],
  );

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.id, label: `${c.countryCode} - ${c.countryName}` })),
    [countries],
  );

  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.id, label: `${s.provinceCode} - ${s.provinceName}` })),
    [states],
  );

  const handleCountryChange = useCallback((countryId: string) => {
    setSelectedCountryId(countryId);
  }, []);

  // --------------------------------------------------------------------------
  // View state
  // --------------------------------------------------------------------------
  type ViewMode = 'list' | 'calendar';
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // When switching to calendar, fetch all holidays for the year (large page)
  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      pageSize: viewMode === 'calendar' ? 500 : 25,
      page: 1,
    }));
  }, [viewMode]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [yearFilter, setYearFilter] = useState<string>(String(CURRENT_YEAR));
  const [officeFilter, setOfficeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // --------------------------------------------------------------------------
  // Server-side search debounce (400ms)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Pagination handlers
  // --------------------------------------------------------------------------
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // --------------------------------------------------------------------------
  // Modal state — Bulk Add
  // --------------------------------------------------------------------------
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const bulkAddMinimizeRef = useRef<(() => void) | null>(null);

  // --------------------------------------------------------------------------
  // Modal state — Edit form
  // --------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Holiday | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Restore handler
  const handleRestoreModal = useCallback(
    (_restoredMode: 'edit', _instanceId?: string, _recordId?: string) => {
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [],
  );

  // --------------------------------------------------------------------------
  // Delete state
  // --------------------------------------------------------------------------
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // --------------------------------------------------------------------------
  // Mutation hooks
  // --------------------------------------------------------------------------
  const { updateHoliday } = useUpdateHoliday();
  const { deleteHoliday } = useDeleteHoliday();

  // --------------------------------------------------------------------------
  // Column definitions for grid
  // --------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<Holiday>[] = useMemo(
    () => [
      {
        id: 'year',
        header: 'Year',
        accessorKey: 'year',
        size: 80,
        enableSorting: true,
      },
      {
        id: 'officeName',
        header: 'Location',
        accessorKey: 'officeName',
        size: 150,
        enableSorting: true,
        cell: ({ row }) => row.original.officeName || '-',
      },
      {
        id: 'holidayDate',
        header: 'Date',
        accessorKey: 'holidayDate',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.holidayDate),
      },
      {
        id: 'day',
        header: 'Day',
        accessorKey: 'holidayDate',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => weekdayShort(row.original.holidayDate, '-'),
      },
      {
        id: 'description',
        header: 'Holiday',
        accessorKey: 'description',
        size: 250,
        enableSorting: true,
      },
      {
        id: 'holidayType',
        header: 'Type',
        accessorKey: 'holidayType',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => {
          const type = row.original.holidayType;
          if (!type) return '-';
          return (
            <span className="badge badge-active" style={{ textTransform: 'capitalize' }}>
              {type.charAt(0) + type.slice(1).toLowerCase()}
            </span>
          );
        },
      },
      {
        id: 'isOptional',
        header: 'Optional',
        accessorKey: 'isOptional',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={`badge badge-${row.original.isOptional ? 'active' : 'inactive'}`}>
            {row.original.isOptional ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 100,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    [],
  );

  // Filter columns by visibility
  const visibleTableColumns = useMemo<SyutGridColumn<Holiday>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns],
  );

  // --------------------------------------------------------------------------
  // Handlers — open modals
  // --------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    setBulkAddOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record: Holiday) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.officeId}-${record.holidayDate?.substring(0, 10)}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(record);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleDeleteClick = useCallback((record: Holiday) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Holiday | null>(null);

  const handleView = useCallback((record: Holiday) => {
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Save handler — PUT (edit only)
  // --------------------------------------------------------------------------
  const handleSaveForm = useCallback(
    async (formData: HolidayFormData) => {
      if (!selectedRecord) return;
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        const payload: UpdateHolidayRequest = {
          year: formData.year,
          officeId: formData.officeId,
          holidayDate: formData.holidayDate,
          description: formData.description,
          holidayType: formData.holidayType || undefined,
          isOptional: formData.isOptional,
          statusCode: formData.statusCode,
          rowVersion: selectedRecord.rowVersion,
        };
        await updateHoliday(selectedRecord.id, payload);
        notificationService.toast.success(`Holiday "${formData.description}" updated.`);
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save holiday. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [selectedRecord, updateHoliday, refetch],
  );

  // --------------------------------------------------------------------------
  // Delete handler
  // --------------------------------------------------------------------------
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteHoliday(deleteTarget.id);
      notificationService.toast.success(
        `Holiday "${deleteTarget.description}" deleted.`,
      );
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete holiday.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteHoliday, refetch]);

  // --------------------------------------------------------------------------
  // Active filter count
  // --------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.year) count++;
    if (queryParams.officeId) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.year, queryParams.officeId, queryParams.statusCode]);

  // --------------------------------------------------------------------------
  // Filter Apply / Clear
  // --------------------------------------------------------------------------
  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      year: yearFilter ? parseInt(yearFilter, 10) : undefined,
      officeId: officeFilter || undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [yearFilter, officeFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setYearFilter(String(CURRENT_YEAR));
    setOfficeFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      year: CURRENT_YEAR,
      includeInactive: true,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: Holiday): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view')) {
        items.push({
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        });
      }
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick],
  );

  // --------------------------------------------------------------------------
  // ModuleHeader action buttons
  // --------------------------------------------------------------------------
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Bulk Add Holidays',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch],
  );

  // --------------------------------------------------------------------------
  // Form initial data (for edit mode)
  // --------------------------------------------------------------------------
  const formInitialData: HolidayFormData | null = useMemo(() => {
    if (!selectedRecord) return null;
    return {
      year: selectedRecord.year,
      officeId: selectedRecord.officeId,
      officeName: selectedRecord.officeName || '',
      holidayDate: selectedRecord.holidayDate?.substring(0, 10) || '',
      description: selectedRecord.description,
      holidayType: selectedRecord.holidayType || '',
      isOptional: selectedRecord.isOptional,
      statusCode: selectedRecord.statusCode as 'ACTIVE' | 'INACTIVE',
    };
  }, [selectedRecord]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Holiday Master"
        description="Manage holidays for all locations by calendar year"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.calendar size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search holiday, location...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar', tooltip: 'Calendar view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={loading ? 'Loading holidays...' : 'No holidays found'}
              />
            )}

            {viewMode === 'calendar' && (() => {
              const calYear = queryParams.year ?? CURRENT_YEAR;
              // Group holidays by month
              const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const holidaysByDate: Record<string, Holiday[]> = {};
              data.forEach((h) => { const d = h.holidayDate?.substring(0, 10); if (d) { (holidaysByDate[d] = holidaysByDate[d] || []).push(h); } });

              return (
                <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                    {monthNames.map((month, mi) => {
                      const firstDay = new Date(calYear, mi, 1);
                      const daysInMonth = new Date(calYear, mi + 1, 0).getDate();
                      const startDow = firstDay.getDay(); // 0=Sun
                      const cells: React.ReactNode[] = [];
                      // Empty cells before first day
                      for (let e = 0; e < startDow; e++) cells.push(<div key={`e${e}`} />);
                      // Day cells
                      for (let d = 1; d <= daysInMonth; d++) {
                        const dateStr = `${calYear}-${String(mi + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const dayHolidays = holidaysByDate[dateStr];
                        const isWeekend = new Date(calYear, mi, d).getDay() === 0 || new Date(calYear, mi, d).getDay() === 6;
                        cells.push(
                          <div
                            key={d}
                            title={dayHolidays ? dayHolidays.map((h) => `${h.description} (${h.officeName || ''})`).join('\n') : ''}
                            style={{
                              padding: '0.25rem',
                              minHeight: '2.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              background: dayHolidays ? 'rgba(22,163,74,0.12)' : isWeekend ? 'rgba(100,116,139,0.06)' : 'transparent',
                              border: dayHolidays ? '1px solid rgba(22,163,74,0.3)' : '1px solid transparent',
                              cursor: dayHolidays ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                              if (dayHolidays && dayHolidays.length === 1) handleEdit(dayHolidays[0]);
                            }}
                          >
                            <div style={{ fontWeight: dayHolidays ? 700 : 400, color: dayHolidays ? '#16a34a' : isWeekend ? 'var(--text-muted)' : 'var(--text-primary)' }}>{d}</div>
                            {dayHolidays && (
                              <div style={{ fontSize: '0.625rem', color: '#16a34a', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {dayHolidays[0].description}
                                {dayHolidays.length > 1 && ` +${dayHolidays.length - 1}`}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div key={month} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.75rem' }}>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.5rem', textAlign: 'center' }}>{month} {calYear}</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', fontSize: '0.7rem', textAlign: 'center' }}>
                            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((dn) => (
                              <div key={dn} style={{ fontWeight: 600, color: 'var(--text-muted)', padding: '0.25rem 0' }}>{dn}</div>
                            ))}
                            {cells}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button
                className="search-panel-close"
                onClick={() => setFilterOpen(false)}
                title="Close filters"
              >
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              {/* Year filter */}
              <div className="filter-section">
                <SyutSelect
                  label="Year"
                  options={YEAR_FILTER_OPTIONS}
                  value={yearFilter}
                  onChange={(val) => setYearFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>

              {/* Location filter */}
              <div className="filter-section">
                <SyutSelect
                  label="Location"
                  options={[{ value: '', label: 'All' }, ...officeOptions]}
                  value={officeFilter}
                  onChange={(val) => setOfficeFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>

              {/* Status filter */}
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Add modal */}
      <HolidayBulkAddModal
        isOpen={bulkAddOpen}
        onClose={() => setBulkAddOpen(false)}
        onSaveComplete={() => refetch()}
        officeOptions={officeOptionsWithGeo}
        countryOptions={countryOptions}
        stateOptions={stateOptions}
        onCountryChange={handleCountryChange}
        tabId={tabId}
        moduleName={moduleName}
        minimizeRef={bulkAddMinimizeRef}
      />

      {/* Edit form modal */}
      <HolidayFormModal
        isOpen={formModalOpen}
        mode="edit"
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Holiday"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.description}" (${formatDate(deleteTarget.holidayDate)})? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Holiday — {viewRecord.description}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Year</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.year || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Location</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.officeName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Date</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.holidayDate)}</div>
                </div>
                <div>
                  <label className="form-label">Day</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{weekdayLong(viewRecord.holidayDate)}</div>
                </div>
                <div>
                  <label className="form-label">Holiday</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.description || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Type</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.holidayType ? viewRecord.holidayType.charAt(0) + viewRecord.holidayType.slice(1).toLowerCase() : '—'}</div>
                </div>
                <div>
                  <label className="form-label">Optional</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.isOptional ? 'Yes' : 'No'}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              {allowedActions.includes('edit') && (
                <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HolidayMasterScreen;
